/**
 * Sidebar ad card component
 * Compact format for inline placement between listings
 */

import { memo, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Sparkles, ArrowRight } from "lucide-react";
import { Ad, trackAdImpression, trackAdClick } from "@/hooks/useAds";
import { Link } from "react-router-dom";

interface AdSidebarProps {
  ad: Ad;
  variant?: 'card' | 'inline' | 'compact';
  className?: string;
}

const AdSidebar = memo(({ ad, variant = 'card', className = "" }: AdSidebarProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Lazy load with intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          trackAdImpression(ad.id);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [ad.id]);

  const handleClick = () => {
    trackAdClick(ad.id);
  };

  const isExternal = ad.link_url?.startsWith('http');

  // Inline variant content
  const inlineContent = (
    <>
      {/* Mini image - high resolution */}
      <div className="w-12 h-12 xs:w-14 xs:h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden shrink-0 bg-white">
        {isVisible && (
          <img
            src={ad.image_url || '/placeholder.svg'}
            alt={ad.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 text-[9px] xs:text-[10px] text-primary font-medium mb-0.5 xs:mb-1">
          <Sparkles className="w-2 h-2 xs:w-2.5 xs:h-2.5" />
          Ad
        </div>
        <h4 className="text-xs xs:text-sm font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors">
          {ad.title}
        </h4>
        <p className="text-[10px] xs:text-xs text-muted-foreground line-clamp-1">
          {ad.description}
        </p>
      </div>

      <ArrowRight className="w-3 h-3 xs:w-4 xs:h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </>
  );

  // Compact variant content
  const compactContent = (
    <>
      <div className="flex items-center gap-1 text-[9px] text-muted-foreground font-medium mb-1">
        <Sparkles className="w-2 h-2" />
        Sponsored
      </div>
      <h4 className="text-xs font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors">
        {ad.title}
      </h4>
    </>
  );

  // Card variant content
  const cardContent = (
    <>
      {/* Image */}
      <div className="relative h-36 xs:h-40 sm:h-44 overflow-hidden bg-muted">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-muted animate-pulse" />
        )}
        
        {isVisible && ad.image_url && ad.image_url !== '/placeholder.svg' && (
          <img
            src={ad.image_url}
            alt={ad.title}
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            className={`w-full h-full object-cover object-center transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}

        {/* Ad badge */}
        <div className="absolute top-2 left-2 xs:top-3 xs:left-3 inline-flex items-center gap-1 bg-primary/10 text-primary text-[9px] xs:text-[10px] font-medium px-1.5 xs:px-2 py-0.5 xs:py-1 rounded-full">
          <Sparkles className="w-2.5 h-2.5 xs:w-3 xs:h-3" />
          <span>Sponsored</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 xs:p-4">
        <h4 className="text-sm xs:text-base font-semibold text-foreground mb-1 xs:mb-1.5 line-clamp-2 group-hover:text-primary transition-colors">
          {ad.title}
        </h4>
        
        {ad.description && (
          <p className="text-xs xs:text-sm text-muted-foreground line-clamp-2 mb-2 xs:mb-3">
            {ad.description}
          </p>
        )}

        <motion.div 
          className="inline-flex items-center gap-1.5 xs:gap-2 text-primary text-xs xs:text-sm font-medium"
          whileHover={{ x: 5 }}
        >
          View Details
          <ExternalLink className="w-3 h-3 xs:w-4 xs:h-4" />
        </motion.div>
      </div>
    </>
  );

  if (variant === 'inline') {
    return (
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
        transition={{ duration: 0.4 }}
        className={`relative ${className}`}
      >
        {isExternal ? (
          <a
            href={ad.link_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="flex items-center gap-2 xs:gap-3 sm:gap-4 p-3 xs:p-4 bg-gradient-to-r from-primary/5 to-cyan-500/5 rounded-lg xs:rounded-xl border border-primary/20 hover:border-primary/40 transition-all group"
          >
            {inlineContent}
          </a>
        ) : (
          <Link
            to={ad.link_url || '/'}
            onClick={handleClick}
            className="flex items-center gap-2 xs:gap-3 sm:gap-4 p-3 xs:p-4 bg-gradient-to-r from-primary/5 to-cyan-500/5 rounded-lg xs:rounded-xl border border-primary/20 hover:border-primary/40 transition-all group"
          >
            {inlineContent}
          </Link>
        )}
      </motion.div>
    );
  }

  if (variant === 'compact') {
    return (
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className={`relative ${className}`}
      >
        {isExternal ? (
          <a
            href={ad.link_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="block p-3 bg-muted/50 rounded-lg border border-border/50 hover:border-primary/30 transition-all group"
          >
            {compactContent}
          </a>
        ) : (
          <Link
            to={ad.link_url || '/'}
            onClick={handleClick}
            className="block p-3 bg-muted/50 rounded-lg border border-border/50 hover:border-primary/30 transition-all group"
          >
            {compactContent}
          </Link>
        )}
      </motion.div>
    );
  }

  // Default card variant
  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
      transition={{ duration: 0.5 }}
      className={`relative overflow-hidden rounded-xl xs:rounded-2xl bg-card border border-border/50 shadow-lg hover:shadow-xl transition-shadow ${className}`}
    >
      {isExternal ? (
        <a
          href={ad.link_url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="block group"
        >
          {cardContent}
        </a>
      ) : (
        <Link
          to={ad.link_url || '/'}
          onClick={handleClick}
          className="block group"
        >
          {cardContent}
        </Link>
      )}
    </motion.div>
  );
});

AdSidebar.displayName = 'AdSidebar';

export default AdSidebar;
